import React, { useState, useEffect } from "react";

// styles
import Styles from "./beneficiariesTab.module.css";

// icons
import { FiEye, FiTrash2, FiShield } from "react-icons/fi";

// actions
import { _getBeneficiaries } from "../../redux/actions";

// redux
import { useDispatch, useSelector } from "react-redux";
import { setBeneficiary } from "../../../../redux/slices/beneficiarySlice";
import { setBeneficiaries } from "../../../../redux/slices/beneficiariesSlice";
import { setCreateBeneficiaryModalData, setCreateBeneficiaryModalOpen } from "../../../modals/beneficiaries/redux/slices/createBeneficiaryModalSlice";

// components
import Button from "../../../../components/Button/Button";

// utils
import { capitalize } from "@mui/material";
import { formatBeneficiaryCellText } from "./helpers";
import { showViewBeneficiaryModal } from "../../../modals/beneficiaries/view/helpers";
import { setDeleteBeneficiaryModalData, setDeleteBeneficiaryModalOpen } from "../../../modals/beneficiaries/redux/slices/deleteBeneficiaryModalSlice";
import { setActivateBeneficiaryModalData, setActivateBeneficiaryModalOpen } from "../../../modals/beneficiaries/redux/slices/activateBeneficiaryModalSlice";

function BeneficiariesTab() {
  // ** Hooks
  const dispatch = useDispatch()

  // ** States
  const beneficiaries = useSelector((state) => state.beneficiaries.value)
  const exchangeName = useSelector((state) => state.exchangeCustomization.value.name)

  document.title = `${exchangeName}: Beneficiaries`

  const limit = 6
  const [page, setPage] = useState(1)
  const [totalRows, setTotalRows] = useState(0)
  const [tableLoading, setTableLoading] = useState(false)
  const currencies = useSelector((state) => state.currencies.value)

  // ----- RESPONSIVENESS STATE ---------
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 767)

  const handleResize = () => {
    setIsMobileView(window.innerWidth < 767)
  }

  useEffect(() => {
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  const fetchBeneficiaries = () => {
    setTableLoading(true)
    _getBeneficiaries(
      {
        page,
        limit
      },
      (data) => {
        setTotalRows(data.total)
        dispatch(setBeneficiaries(data.data))
        setTableLoading(false)
      },
      (err) => {
        setTableLoading(false)
      }
    )
  }

  const handlePageChange = (event, value) => {
    setPage(value)
  }

  useEffect(() => {
    fetchBeneficiaries()
  }, [page])

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'var(--success-color)'
      case 'pending':
        return 'var(--warning-color)'
      case 'rejected':
      case 'in progress':
      case 'frb_processing':
        return 'var(--danger-color)'
      default:
        return 'var(--gray-color)'
    }
  }

  const totalPages = Math.ceil(totalRows / limit)
  const startIndex = (page - 1) * limit
  const endIndex = Math.min(startIndex + limit, totalRows)

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1)
    }
  }

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1)
    }
  }

  return (
    <div className={Styles.container}>
      <div className={Styles.card}>
        <div className={Styles.content}>
          <div className={Styles.modernBeneficiariesContainer}>
            <h5>Beneficiaries</h5>

            {/* Header */}
            <div className={Styles.beneficiariesHeader}>
              <Button
                text="Create new"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "8px",
                  padding: "10px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  width: "auto",
                  minWidth: "auto"
                }}
                onClick={() => {
                  dispatch(setCreateBeneficiaryModalOpen(true))
                  dispatch(setCreateBeneficiaryModalData(fetchBeneficiaries))
                }}
              />
            </div>

            {/* Beneficiaries Grid */}
            {tableLoading ? (
              <div className={Styles.loadingGrid}>
                {Array.from({ length: limit }).map((_, index) => (
                  <div key={index} className={Styles.beneficiaryCardSkeleton}>
                    {/* Card Header Shimmer */}
                    <div className={Styles.skeletonHeader}>
                      <div className={Styles.skeletonCoinSection}>
                        <div className={`${Styles.shimmerEffect} ${Styles.skeletonIcon}`}></div>
                        <div className={Styles.skeletonCoinInfo}>
                          <div className={`${Styles.shimmerEffect} ${Styles.skeletonTitle}`}></div>
                          <div className={`${Styles.shimmerEffect} ${Styles.skeletonTag}`}></div>
                        </div>
                      </div>
                      <div className={`${Styles.shimmerEffect} ${Styles.skeletonStatus}`}></div>
                    </div>

                    {/* Card Content Shimmer */}
                    <div className={Styles.skeletonContent}>
                      <div className={Styles.skeletonField}>
                        <div className={`${Styles.shimmerEffect} ${Styles.skeletonLabel}`}></div>
                        <div className={`${Styles.shimmerEffect} ${Styles.skeletonValue}`}></div>
                      </div>
                      <div className={Styles.skeletonField}>
                        <div className={`${Styles.shimmerEffect} ${Styles.skeletonLabel}`}></div>
                        <div className={`${Styles.shimmerEffect} ${Styles.skeletonValue}`}></div>
                      </div>
                    </div>

                    {/* Card Actions Shimmer */}
                    <div className={Styles.skeletonActions}>
                      <div className={`${Styles.shimmerEffect} ${Styles.skeletonButton}`}></div>
                      <div className={`${Styles.shimmerEffect} ${Styles.skeletonButton}`}></div>
                      <div className={`${Styles.shimmerEffect} ${Styles.skeletonButton}`}></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={Styles.beneficiariesGrid}>
                {beneficiaries.map((beneficiary) => {
                  const coin = currencies?.find((el) => el.id === beneficiary.currency)

                  return (
                    <div key={beneficiary.id} className={Styles.beneficiaryCard}>
                      {/* Card Header */}
                      <div className={Styles.cardHeader}>
                        <div className={Styles.coinSection}>
                          <div className={Styles.coinIconWrapper}>
                            <img
                              src={coin?.icon_url}
                              alt={coin?.name}
                              className={Styles.coinIcon}
                            />
                          </div>
                          <div className={Styles.coinInfo}>
                            <h3 className={Styles.coinName}>{coin?.name || beneficiary.currency}</h3>
                            <div className={Styles.coinTag}>
                              {beneficiary.currency?.toUpperCase()}
                            </div>
                          </div>
                        </div>

                        {/* Status Badge */}
                        <div
                          className={Styles.statusBadge}
                          style={{
                            backgroundColor: `${getStatusColor(beneficiary.state)}20`,
                            borderColor: `${getStatusColor(beneficiary.state)}40`
                          }}
                        >
                          <div
                            className={Styles.statusDot}
                            style={{ backgroundColor: getStatusColor(beneficiary.state) }}
                          />
                          <span
                            className={Styles.statusText}
                            style={{ color: getStatusColor(beneficiary.state) }}
                          >
                            {capitalize(beneficiary.state)}
                          </span>
                        </div>
                      </div>

                      {/* Card Content */}
                      <div className={Styles.cardContent}>
                        <div className={Styles.fieldGroup}>
                          <label className={Styles.fieldLabel}>Beneficiary Name</label>
                          <p className={Styles.fieldValue}>{beneficiary.name}</p>
                        </div>

                        {beneficiary.description && (
                          <div className={Styles.fieldGroup}>
                            <label className={Styles.fieldLabel}>Description</label>
                            <p className={Styles.fieldValueSecondary}>
                              {formatBeneficiaryCellText(beneficiary.description)}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Card Actions */}
                      <div className={Styles.cardActions}>
                        {beneficiary.state === "Pending" && (
                          <button
                            className={`${Styles.actionButton} ${Styles.activateButton}`}
                            onClick={() => {
                              dispatch(setActivateBeneficiaryModalOpen(true))
                              dispatch(setActivateBeneficiaryModalData(fetchBeneficiaries))
                              dispatch(setBeneficiary({ id: beneficiary.id }))
                            }}
                          >
                            <FiShield size={12} />
                            Activate
                          </button>
                        )}

                        <button
                          className={`${Styles.actionButton} ${Styles.viewButton}`}
                          onClick={() => showViewBeneficiaryModal(beneficiary)}
                        >
                          <FiEye size={12} />
                          View
                        </button>

                        <button
                          className={`${Styles.actionButton} ${Styles.deleteButton}`}
                          onClick={() => {
                            dispatch(setDeleteBeneficiaryModalOpen(true))
                            dispatch(setDeleteBeneficiaryModalData(fetchBeneficiaries))
                            dispatch(setBeneficiary({ id: beneficiary.id }))
                          }}
                        >
                          <FiTrash2 size={12} />
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Empty State */}
            {!tableLoading && beneficiaries.length === 0 && (
              <div className={Styles.emptyState}>
                <p>No beneficiaries found</p>
                <span>Create your first beneficiary to get started</span>
              </div>
            )}

            {/* Pagination */}
            {totalRows > 0 && (
              <div className={Styles.paginationContainer}>
                <span className={Styles.paginationInfo}>
                  {startIndex + 1}-{endIndex} of {totalRows}
                </span>

                <div className={Styles.paginationControls}>
                  <button
                    onClick={handlePrevPage}
                    disabled={page === 1}
                    className={`${Styles.paginationButton} ${page === 1 ? Styles.disabled : ''}`}
                  >
                    ←
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`${Styles.paginationButton} ${page === pageNum ? Styles.active : ''}`}
                    >
                      {pageNum}
                    </button>
                  ))}

                  <button
                    onClick={handleNextPage}
                    disabled={page === totalPages}
                    className={`${Styles.paginationButton} ${page === totalPages ? Styles.disabled : ''}`}
                  >
                    →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BeneficiariesTab
